/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Module Features Hooks — Org User Feature Gating
 * ============================================================================
 *
 * Provides hooks for checking whether the current organization has access
 * to specific module features. Used by sidebar navigation and route guards
 * to conditionally show/hide premium functionality.
 *
 * ============================================================================
 */

import { useQuery } from '@tanstack/react-query';
import { get } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';
import { useCallback } from 'react';

/**
 * Module access info returned by GET /api/v1/modules/my-modules
 */
interface ModuleAccessInfo {
  code: string;
  name: string;
  version: string;
  description: string;
  apiBasePath: string;
  features: string[];
  required: boolean;
  icon: string;
  hasAccess: boolean;
  licenseType: string | null;
  expiresAt: string | null;
  enabledFeatures: string[];
}

// Query key
export const myModulesKeys = {
  all: ['my-modules'] as const,
};

/**
 * Hook to fetch the current organization's licensed modules.
 */
export function useMyModules() {
  return useQuery<ModuleAccessInfo[]>({
    queryKey: myModulesKeys.all,
    queryFn: async () => {
      const response = await get<ApiResponse<ModuleAccessInfo[]>>('/modules/my-modules');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for checking feature access in the current organization.
 *
 * Returns `true` while loading to avoid sidebar flicker and premature
 * route redirects. Once data arrives, actual gating applies.
 */
export function useHasFeature() {
  const { data: modules, isLoading } = useMyModules();

  const hasFeature = useCallback(
    (moduleCode: string, featureCode: string): boolean => {
      // Optimistic: return true while loading to avoid flicker
      if (isLoading || !modules) return true;

      const mod = modules.find(
        (m) => m.code.toUpperCase() === moduleCode.toUpperCase()
      );

      if (!mod || !mod.hasAccess) return false;

      // If module has no features defined, all features are implicitly enabled
      if (!mod.features || mod.features.length === 0) return true;

      return (mod.enabledFeatures || [])
        .map((f) => f.toUpperCase())
        .includes(featureCode.toUpperCase());
    },
    [modules, isLoading]
  );

  return { hasFeature, isLoading };
}
